import React, { useState, useContext, useEffect, useMemo, createContext } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router';
import { DashboardIcon, ExitIcon } from '@radix-ui/react-icons';
import {
  WrenchIcon,
  BanknotesIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  CircleStackIcon,
  CalendarDaysIcon,
  MapPinIcon,
  TicketIcon,
  EnvelopeIcon,
  ArrowsRightLeftIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import NavBar from './NavBar';
import { AppContext } from '../../context/provider';
import { Building2, Database, Receipt } from 'lucide-react';
import { access, shortAccess, shortFeatureNames } from '../../util/FeatureTypes';
import getFeatureFlag from '../../util/getFeatureFlag';
import ROLE_IDS from '../../util/RoleIds';

// Create Layout Context to expose visible nav links to children
export const LayoutContext = createContext();

function MainApp({ children, userEmail, logoutFunction, authEnabled }) {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);
  const [open, setOpen] = useState(!isSmallScreen);
  const [drawerWidth, setDrawerWidth] = useState(isSmallScreen ? '0' : '260px');
  const { permissions, violationsAvailable } = useContext(AppContext);

  const {
    hasEnforcementPermission,
    hasRateSettingsPermission,
    hasPromoCodePermission,
    hasParkingPassesPermission,
    hasRefundPortalPermission,
    hasUserPermission,
    hasUserRolePermission,
    hasSettledDashboardPermission,
    hasRealTimeDashboardPermission,
    hasLocationsPermissions,
    hasTenantValidationPermission,
    hasBulkGenerationPermission,
    superuser,
  } = permissions || {};


  const IS_PROD = window.location.hostname === 'lightningpayadmin.propark.com' ? true : false;

  const parkingPassesEnabled = getFeatureFlag('PARKING_PASSES_ENABLED');

  const navLinks = useMemo(() => ({
    realTimeDashboard: {
      visible: authEnabled && hasRealTimeDashboardPermission,
      path: '/',
      label: 'Real Time Dashboard',
    },
    settledDashboard: {
      visible: authEnabled && hasSettledDashboardPermission,
      path: '/settled-dashboard',
      label: 'Settled Dashboard',
    },
    enforcement: {
      visible: hasEnforcementPermission,
      path: '/list',
      label: 'Enforcement',
    },
    parkingPasses: {
      visible: parkingPassesEnabled && authEnabled && hasParkingPassesPermission,
      path: '/passes',
      label: 'Parking Passes',
    },
    configureRates: {
      visible: authEnabled && hasRateSettingsPermission,
      path: '/rate-selection',
      label: 'Configure Rates',
    },
    rateChanges: {
      visible: authEnabled && hasRateSettingsPermission,
      path: '/rates',
      label: 'Rate Changes',
    },
    locations: {
      visible: authEnabled && hasLocationsPermissions,
      path: '/locations',
      label: 'Locations',
    },
    promoCodes: {
      visible: authEnabled && hasPromoCodePermission,
      path: '/promo-codes',
      label: 'Promo Codes',
    },
    connectAccounts: {
      visible: authEnabled && (superuser || permissions?.defaultRole === ROLE_IDS.ACCOUNTANT),
      path: '/connect-accounts',
      label: 'Bank Accounts',
    },
    transactions: {
      visible: authEnabled && hasRefundPortalPermission,
      path: '/transactions',
      label: 'Transactions',
    },
    users: {
      visible: authEnabled && hasUserPermission,
      path: '/user-config',
      label: 'Users',
    },
    userRoles: {
      visible: authEnabled && hasUserRolePermission,
      path: '/role-config',
      label: 'User Roles',
    },
    enforcementLite: {
      visible: authEnabled && superuser && violationsAvailable,
      path: '/enforcement-lite',
      label: 'Enforcement Lite',
    },
    mailing: {
      visible: authEnabled && superuser,
      path: '/mailing',
      label: 'Mailing',
    },
    tenantManagement: {
      visible: authEnabled && (superuser || hasBulkGenerationPermission),
      path: '/tenant-management',
      label: 'Tenant Management',
    },
    tenantValidations: {
      visible: authEnabled && hasTenantValidationPermission,
      path: '/tenant-validations',
      label: 'Tenant Validations',
    },
    databaseCopy: {
      visible: authEnabled && !IS_PROD && superuser,
      path: '/database-copy',
      label: 'Database Copy',
    },
  }), [
    authEnabled,
    hasRealTimeDashboardPermission,
    hasSettledDashboardPermission,
    hasEnforcementPermission,
    hasRateSettingsPermission,
    hasLocationsPermissions,
    hasPromoCodePermission,
    hasParkingPassesPermission,
    parkingPassesEnabled,
    hasRefundPortalPermission,
    hasUserPermission,
    hasUserRolePermission,
    superuser,
    violationsAvailable,
    hasBulkGenerationPermission,
    hasTenantValidationPermission,
    permissions,
    IS_PROD,
  ]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth < 1200;
      setIsSmallScreen(smallScreen);
      if (smallScreen && open) {
        setOpen(false);
        setDrawerWidth('0');
      } else if (!smallScreen && !open) {
        setOpen(true);
        setDrawerWidth('260px');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open]);

  const featureCSS =
    'flex flex-row w-60 leading-6 rounded-md mb-1 py-2.5 px-4 font-medium text-sm text-[#ffffffb3] hover:text-white hover:bg-[#ffffff0d]';
  const activeFeatureCSS = 'flex flex-row w-60 leading-6 rounded-md mb-1 py-2.5 px-4 font-medium text-sm text-white bg-[#ffffff1a]';
  const iconCSS = 'mr-4 w-6 h-6';

  const handleDrawer = () => {
    setOpen(prev => {
      if (prev) {
        setDrawerWidth(0);
      } else {
        setDrawerWidth('260px');
      }
      return !prev;
    });
  };

  const handleDrawerFromScreen = () => {
    if (isSmallScreen && open) {
      setOpen(false);
      setDrawerWidth(0);
    }
  };

  if (!userEmail) {
    return (
      <LayoutContext.Provider value={{ navLinks: {} }}>
        <div>
          <div className="shadow-lg">
            <NavBar handleDrawer={null} homePageHandler={null} />
          </div>
          <div>{children}</div>
        </div>
      </LayoutContext.Provider>
    );
  }

  return (
    <LayoutContext.Provider value={{ navLinks }}>
      <div>
        <div
          className="fixed z-10 text-white/70 float-left bg-gray-900 h-screen flex"
          style={{
            width: drawerWidth,
            transition: 'width 0.25s',
          }}>
          <DrawerContent />
        </div>
        <div
          onClick={handleDrawerFromScreen}
          className="h-screen float-right"
          style={{
            width: `${drawerWidth && !isSmallScreen ? `calc(100% - ${drawerWidth})` : '100%'}`,
            transition: 'width 0.25s',
          }}>
          <div className="fixed mb-4 shadow-lg w-full bg-white z-50">
            <NavBar
              open={open}
              handleDrawer={handleDrawer}
              width={`${drawerWidth && !isSmallScreen ? `calc(100% - ${drawerWidth})` : '100%'}`}
              hasRealTimeDashboardPermission={hasRealTimeDashboardPermission || false}
            />
          </div>
          <div className="py-4 px-4 mt-[60px]">{children}</div>
        </div>
      </div>
    </LayoutContext.Provider>
  );

  function DrawerContent() {
    return (
      <div className="text-xs font-bold overflow-y-auto overflow-x-hidden w-full">
        <div className="flex flex-col items-center mb-4 border-neutral-800 border-b pb-4">
          <div className="badge flex items-center justify-center font-medium text-[#5FDAFC] text-xl mb-4 leading-[44px] border-neutral-800 border-b w-full h-[60px]">
            Lightning Pay Admin
          </div>
          <Avatar className="w-20 h-20 mb-2 grow">
            <AvatarFallback className="bg-orange-600 text-3xl">
              {`${userEmail.charAt(0)?.toUpperCase()}${userEmail.split('.')[1].charAt(0).toUpperCase()}` || 'U'}
            </AvatarFallback>
          </Avatar>
          <p className="text-white w-60 overflow-hidden whitespace-nowrap text-ellipsis text-lg capitalize px-2">
            {userEmail.split('@')?.shift().replace('.', ' ') || 'User'}
          </p>
          <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger>
                <p className="w-60 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-[#94a3b8] px-2">
                  {userEmail || 'email@projectinflection.com'}
                </p>
              </TooltipTrigger>
              <TooltipContent className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade text-violet11 select-none rounded-[4px] bg-white px-[15px] py-[10px] text-[14px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity] font-light text-black">
                {userEmail || 'email@projectinflection.com'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="ml-[10px]">
          {navLinks.realTimeDashboard?.visible && (
            <NavLink end to="/" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <DashboardIcon className={iconCSS} />
              <p className="">{navLinks.realTimeDashboard?.label}</p>
            </NavLink>
          )}
          {navLinks.settledDashboard?.visible && (
            <NavLink to="/settled-dashboard" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <Receipt className={iconCSS} />
              <p>{navLinks.settledDashboard?.label}</p>
            </NavLink>
          )}
          {navLinks.enforcement?.visible && (
            <NavLink to="/list" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <CircleStackIcon className={iconCSS} />
              <p>{navLinks.enforcement?.label}</p>
            </NavLink>
          )}
          {navLinks.parkingPasses?.visible && (
            <NavLink to="/passes" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <TicketIcon className={iconCSS} />
              <p>{navLinks.parkingPasses?.label}</p>
            </NavLink>
          )}
          {navLinks.configureRates?.visible && (
            <NavLink to="/rate-selection" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <WrenchIcon className={iconCSS} />
              <p>{navLinks.configureRates?.label}</p>
            </NavLink>
          )}
          {navLinks.rateChanges?.visible && (
            <NavLink to="/rates" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <CalendarDaysIcon className={iconCSS} />
              <p>{navLinks.rateChanges?.label}</p>
            </NavLink>
          )}
          {navLinks.locations?.visible && (
            <NavLink to="/locations" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <MapPinIcon className={iconCSS} />
              <p>{navLinks.locations?.label}</p>
            </NavLink>
          )}
          {navLinks.connectAccounts?.visible && (
            <NavLink to="/connect-accounts" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <Building2 className={iconCSS} />
              <p>{navLinks.connectAccounts?.label}</p>
            </NavLink>
          )}
          {navLinks.promoCodes?.visible && (
            <NavLink to="/promo-codes" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <BanknotesIcon className={iconCSS} />
              <p>{navLinks.promoCodes?.label}</p>
            </NavLink>
          )}
          {navLinks.transactions?.visible && (
            <NavLink to="/transactions" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <ArrowsRightLeftIcon className={iconCSS} />
              <p>{navLinks.transactions?.label}</p>
            </NavLink>
          )}
          {navLinks.users?.visible && (
            <NavLink to="/user-config" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <UsersIcon className={iconCSS} />
              <p>{navLinks.users?.label}</p>
            </NavLink>
          )}
          {navLinks.userRoles?.visible && (
            <NavLink to="/role-config" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <WrenchScrewdriverIcon className={iconCSS} />
              <p>{navLinks.userRoles?.label}</p>
            </NavLink>
          )}
          {navLinks.enforcementLite?.visible && (
            <NavLink to="/enforcement-lite" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <ClipboardDocumentListIcon className={iconCSS} />
              <p>{navLinks.enforcementLite?.label}</p>
            </NavLink>
          )}
          {navLinks.mailing?.visible && (
            <NavLink to="/mailing" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <EnvelopeIcon className={iconCSS} />
              <p>{navLinks.mailing?.label}</p>
            </NavLink>
          )}
          {navLinks.tenantManagement?.visible && (
            <NavLink to="/tenant-management" className={({ isActive }) => isActive ? activeFeatureCSS : featureCSS}>
              <UsersIcon className={iconCSS} />
              <p>{navLinks.tenantManagement?.label}</p>
            </NavLink>
          )}
          {navLinks.tenantValidations?.visible && (
            <NavLink to="/tenant-validations" className={({ isActive }) => isActive ? activeFeatureCSS : featureCSS}>
              <ClipboardDocumentCheckIcon className={iconCSS} />
              <p>{navLinks.tenantValidations?.label}</p>
            </NavLink>
          )}
          {navLinks.databaseCopy?.visible && (
            <NavLink to="/database-copy" className={({ isActive }) => (isActive ? activeFeatureCSS : featureCSS)}>
              <Database className={iconCSS} />
              <p>{navLinks.databaseCopy?.label}</p>
            </NavLink>
          )}
          <NavLink
            to="/"
            className={featureCSS}
            onClick={() => {
              logoutFunction();
            }}>
            <ExitIcon className={iconCSS} />
            <p>Logout</p>
          </NavLink>
        </div>
      </div>
    );
  }
}

MainApp.propTypes = {
  children: PropTypes.node.isRequired,
  userEmail: PropTypes.string,
  logoutFunction: PropTypes.func.isRequired,
  authEnabled: PropTypes.bool.isRequired,
};

export default MainApp;
